import React from 'react'
import { Box, HStack, IconButton, Text, VStack, type BoxProps } from '@chakra-ui/react'
import { Check, Pencil } from 'lucide-react'
import { useThemeColors } from '@/hooks/useThemeColors'

export type TimelineChip = {
  label: string
  tone?: 'gray' | 'green' | 'blue' | 'red'
}

export type TimelineItem = {
  id: string
  actor: string
  action: string
  timeLabel?: string
  type?: 'default' | 'done' | 'update'
  chips?: TimelineChip[]
  onEdit?: () => void
}

type Props = {
  items: TimelineItem[]
} & BoxProps

function chipStyle(tone: TimelineChip['tone'], colors: ReturnType<typeof useThemeColors>) {
  switch (tone) {
    case 'green':
      return { bg: 'green.50', color: 'green.700', border: 'green.200' }
    case 'blue':
      return { bg: 'blue.50', color: 'blue.700', border: 'blue.200' }
    case 'red':
      return { bg: 'red.50', color: 'red.700', border: 'red.200' }
    default:
      return { bg: colors.inputBg ?? 'gray.50', color: colors.textSecondary, border: colors.borderLight }
  }
}

function ActorBadge({
  actor,
  type,
  colors,
}: {
  actor: string
  type?: TimelineItem['type']
  colors: ReturnType<typeof useThemeColors>
}) {
  if (type === 'done') {
    return (
      <Box
        w="32px"
        h="32px"
        borderRadius="full"
        bg="gray.900"
        color="white"
        display="grid"
        placeItems="center"
        flexShrink={0}
      >
        <Check size={14} />
      </Box>
    )
  }

  if (type === 'update') {
    const safe = (actor ?? '').trim()
    const initial = (safe[0] ?? '•').toUpperCase()
    return (
      <Box
        w="32px"
        h="32px"
        borderRadius="full"
        bg="blue.50"
        border="1px solid"
        borderColor="blue.200"
        display="grid"
        placeItems="center"
        flexShrink={0}
      >
        <Text fontWeight="800" color="blue.600" fontSize="sm" lineHeight="1">
          {initial}
        </Text>
      </Box>
    )
  }

  const safe = (actor ?? '').trim()
  const initial = (safe[0] ?? '•').toUpperCase()

  return (
    <Box
      w="32px"
      h="32px"
      borderRadius="full"
      bg={colors.inputBg ?? 'gray.50'}
      border="1px solid"
      borderColor={colors.borderLight}
      display="grid"
      placeItems="center"
      flexShrink={0}
    >
      <Text fontWeight="800" color={colors.textSecondary} fontSize="sm" lineHeight="1">
        {initial}
      </Text>
    </Box>
  )
}

export default function Timeline({ items, ...boxProps }: Props) {
  const colors = useThemeColors()
  const count = items?.length ?? 0

  return (
    <Box
      bg={colors.cardBg}
      borderRadius="lg"
      border="1px solid"
      borderColor={colors.border}
      overflow="hidden"
      display="flex"
      flexDir="column"
      minH={0}
      {...boxProps}
    >
      {/* Header (match TicketProperties) */}
      <Box px={4} py={3} borderBottom="1px solid" borderColor={colors.borderLight}>
        <Text fontWeight="700" color={colors.primary}>
          Activity Log
        </Text>
      </Box>

      {/* Body — scrollable when more than 6 items */}
      <Box px={4} py={4} flex="1" minH={0} overflowY="auto">
        {count ? (
          <VStack align="stretch" spacing={0}>
            {items.map((it, idx) => {
              const isLast = idx === count - 1

              return (
                <HStack key={it.id} align="stretch" spacing={3} position="relative" role="group" pb={isLast ? 0 : 4}>
                  {/* left column – stretches full row height */}
                  <Box w="32px" minW="32px" position="relative">
                    <ActorBadge actor={it.actor} type={it.type} colors={colors} />

                    {!isLast && (
                      <Box
                        position="absolute"
                        top="36px"
                        left="50%"
                        transform="translateX(-50%)"
                        w="2px"
                        bottom="0"
                        bg={colors.borderLight}
                        borderRadius="full"
                      />
                    )}
                  </Box>

                  {/* content */}
                  <Box flex="1" minW={0} pt="1px">
                    {it.type === 'update' ? (
                      <VStack align="start" spacing={1} minW={0}>
                        <Text fontSize="xs" color={colors.textMuted} fontWeight="600">
                          {it.timeLabel}
                        </Text>
                        <Text fontSize="sm" color={colors.text} whiteSpace="pre-wrap">
                          {it.action}
                        </Text>
                      </VStack>
                    ) : (
                      <VStack align="start" spacing={1.5} minW={0}>
                        <Text fontSize="sm" color={colors.text} lineHeight="short">
                          <Text as="span" fontWeight="700">
                            {(it.actor ?? '').trim()}
                          </Text>{' '}
                          <Text as="span" fontWeight="500" color={colors.textSecondary}>
                            {it.action}
                          </Text>
                        </Text>

                        {it.chips?.length ? (
                          <HStack spacing={2} flexWrap="wrap">
                            {it.chips.map((c, i) => {
                              const s = chipStyle(c.tone, colors)
                              return (
                                <Box
                                  key={`${it.id}-chip-${i}`}
                                  px={2.5}
                                  py={0.5}
                                  borderRadius="md"
                                  bg={s.bg}
                                  border="1px solid"
                                  borderColor={s.border}
                                >
                                  <Text fontSize="xs" fontWeight="700" color={s.color}>
                                    {c.label}
                                  </Text>
                                </Box>
                              )
                            })}
                          </HStack>
                        ) : null}

                        {it.timeLabel ? (
                          <Text fontSize="xs" color={colors.textMuted} fontWeight="600">
                            {it.timeLabel}
                          </Text>
                        ) : null}
                      </VStack>
                    )}
                  </Box>

                  {/* Edit icon on hover for update items */}
                  {it.onEdit && (
                    <IconButton
                      aria-label="Edit update"
                      icon={<Pencil size={13} />}
                      variant="ghost"
                      size="xs"
                      color="blue.400"
                      bg="transparent"
                      _hover={{ color: 'white', bg: 'blue.500' }}
                      opacity={0}
                      _groupHover={{ opacity: 1 }}
                      onClick={it.onEdit}
                      mt="2px"
                      borderRadius="md"
                      minW="24px"
                      h="24px"
                    />
                  )}
                </HStack>
              )
            })}
          </VStack>
        ) : (
          <Text fontSize="sm" color={colors.textSecondary}>
            No recent activity.
          </Text>
        )}
      </Box>
    </Box>
  )
}
